import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Toolbar, ToolbarSelect, fmtINR } from "./_shared";

export default function Inspections() {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filter, setFilter] = useState("scheduled");
  const [form, setForm] = useState({ vehicle_id: "", scheduled_at: "", executive_name: "", notes: "" });

  const load = () => Promise.all([
    api.get("/admin/inspections").then(r => setItems(r.data)),
    api.get("/admin/seller-leads").then(r => setVehicles(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => filter === "all" ? items : items.filter(i => i.status === filter), [items, filter]);

  const create = async () => {
    if (!form.vehicle_id || !form.scheduled_at || !form.executive_name) { toast.error("All fields required"); return; }
    await api.post("/admin/inspections", form);
    toast.success("Inspection scheduled");
    setForm({ vehicle_id: "", scheduled_at: "", executive_name: "", notes: "" });
    load();
  };

  const update = async (iid, patch) => {
    await api.patch(`/admin/inspections/${iid}`, patch);
    toast.success("Updated");
    load();
  };

  return (
    <div data-testid="admin-inspections">
      <PageHeader title="Inspection Operations" subtitle="Field Executives" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#2A2A2A]">
        {/* Scheduler */}
        <div className="bg-[#0E0E0E] p-5">
          <div className="qd-overline mb-3">Schedule New</div>
          <div className="space-y-3">
            <div>
              <label className="qd-label">Vehicle</label>
              <select value={form.vehicle_id} onChange={(e) => setForm({...form, vehicle_id: e.target.value})} className="qd-input-block" data-testid="ins-vehicle">
                <option value="">Select vehicle…</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{`${v.registration_number} · ${v.make} ${v.model}`}</option>)}
              </select>
            </div>
            <div>
              <label className="qd-label">Date / Time</label>
              <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({...form, scheduled_at: e.target.value})} className="qd-input-block" data-testid="ins-time" />
            </div>
            <div>
              <label className="qd-label">Executive</label>
              <input value={form.executive_name} onChange={(e) => setForm({...form, executive_name: e.target.value})} className="qd-input-block" placeholder="e.g. Karthik" data-testid="ins-exec" />
            </div>
            <div>
              <label className="qd-label">Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="qd-input-block min-h-[60px]" data-testid="ins-notes" />
            </div>
            <button onClick={create} className="btn-primary w-full" data-testid="ins-create">Schedule Inspection</button>
          </div>
        </div>

        {/* List */}
        <div className="bg-[#0E0E0E] p-5 lg:col-span-2">
          <Toolbar>
            <ToolbarSelect value={filter} onChange={setFilter} testid="ins-filter" options={[
              {v:"scheduled",l:"Pending"},{v:"completed",l:"Completed"},{v:"cancelled",l:"Cancelled"},{v:"all",l:"All"}
            ]} />
            <div className="font-mono-data text-[10px] tracking-widest text-white/40 ml-auto">{filtered.length} inspections</div>
          </Toolbar>
          <div className="overflow-x-auto">
            <table className="term-table w-full">
              <thead><tr><th>Scheduled</th><th>Vehicle</th><th>Executive</th><th>Status</th><th>Notes</th><th>Action</th></tr></thead>
              <tbody>
                {filtered.map(i => (
                  <tr key={i.id} data-testid={`ins-row-${i.id}`}>
                    <td className="text-white/80">{new Date(i.scheduled_at).toLocaleString()}</td>
                    <td className="text-white">{i.vehicle ? `${i.vehicle.registration_number} · ${i.vehicle.make} ${i.vehicle.model}` : i.vehicle_id.slice(0,8)+"…"}</td>
                    <td className="text-white/70">{i.executive_name}</td>
                    <td><span className="text-[10px] font-mono-data tracking-widest uppercase" style={{ color: i.status === "completed" ? "#22C55E" : i.status === "cancelled" ? "#808080" : "#F5A623" }}>{i.status}</span></td>
                    <td className="text-white/55 max-w-[200px] truncate">{i.notes || "—"}</td>
                    <td className="py-2 flex gap-1">
                      {i.status === "scheduled" && (
                        <>
                          <button onClick={() => update(i.id, { status: "completed" })} className="text-[10px] font-display tracking-widest px-2 py-1 border border-green-500/40 text-green-400" data-testid={`ins-complete-${i.id}`}>DONE</button>
                          <button onClick={() => update(i.id, { status: "cancelled" })} className="text-[10px] font-display tracking-widest px-2 py-1 border border-[#808080]/40 text-white/60" data-testid={`ins-cancel-${i.id}`}>CANCEL</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={6} className="text-center text-white/40 py-8">No inspections</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
